<template>
  <div class="flex flex-col gap-6">

    <!-- Header -->
    <AppPageHeader :title="t('shortLeaveSessions.title')" :description="t('shortLeaveSessions.description')">
      <template #actions>
        <span class="text-sm text-muted-foreground font-medium">{{ pagination.total }} {{ t('shortLeaveSessions.sessions') }}</span>
      </template>
    </AppPageHeader>

    <!-- Filters -->
    <AppCard>
      <div class="p-4">
        <div class="flex flex-wrap gap-3 items-end">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">{{ t('shortLeaveSessions.from') }}</label>
            <input
              type="date"
              v-model="filters.start_date"
              class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">{{ t('shortLeaveSessions.to') }}</label>
            <input
              type="date"
              v-model="filters.end_date"
              class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-muted-foreground">{{ t('shortLeaveSessions.status') }}</label>
            <select
              v-model="filters.status"
              class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">{{ t('shortLeaveSessions.allStatuses') }}</option>
              <option value="active">{{ t('shortLeaveSessions.active') }}</option>
              <option value="completed">{{ t('shortLeaveSessions.completed') }}</option>
              <option value="expired">{{ t('shortLeaveSessions.expired') }}</option>
            </select>
          </div>
          <div class="flex gap-2">
            <AppButton @click="applyFilters">{{ t('shortLeaveSessions.apply') }}</AppButton>
            <AppButton variant="outline" @click="resetFilters">{{ t('shortLeaveSessions.reset') }}</AppButton>
          </div>
        </div>
      </div>
    </AppCard>

    <!-- Table -->
    <AppCard>
      <AppTable
        :columns="columns"
        :rows="sessions"
        :loading="loading"
        rowKey="id"
        :emptyText="t('shortLeaveSessions.noSessions')"
        @row-click="openDetail"
      >
        <!-- Employee cell -->
        <template #cell-employee_name="{ row }">
          <div class="flex items-center gap-2.5">
            <div class="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
              {{ (row.employee_name || '?').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() }}
            </div>
            <div>
              <p class="text-sm font-medium text-foreground leading-tight">{{ row.employee_name }}</p>
              <p v-if="row.employee_id" class="text-[11px] text-muted-foreground">{{ row.employee_id }}</p>
            </div>
          </div>
        </template>

        <!-- Date cell -->
        <template #cell-date="{ row }">
          <span class="text-sm text-foreground">{{ formatDate(row.date) }}</span>
        </template>

        <!-- Leave In cell -->
        <template #cell-leave_in_time="{ row }">
          <span class="text-sm font-mono text-foreground">{{ formatTime(row.leave_in_time) }}</span>
        </template>

        <!-- Leave Out cell -->
        <template #cell-leave_out_time="{ row }">
          <span v-if="row.leave_out_time" class="text-sm font-mono text-foreground">{{ formatTime(row.leave_out_time) }}</span>
          <span v-else class="text-xs text-amber-600 font-medium italic">{{ t('shortLeaveSessions.stillOut') }}</span>
        </template>

        <!-- Duration cell -->
        <template #cell-duration_minutes="{ row }">
          <div>
            <span v-if="row.duration_minutes != null" class="text-sm text-foreground">{{ formatDuration(row.duration_minutes) }}</span>
            <span v-else-if="row.status === 'active'" class="text-xs text-amber-500 italic">{{ t('shortLeaveSessions.inProgress') }}</span>
            <span v-else class="text-sm text-muted-foreground">—</span>
            <span v-if="row.grace_period_applied" class="block text-[10px] text-emerald-600 font-medium">{{ t('shortLeaveSessions.grace') }}</span>
          </div>
        </template>

        <!-- Photos cell -->
        <template #cell-photos="{ row }">
          <div class="flex items-center gap-1.5">
            <img
              v-if="row.leave_in_photo_url"
              :src="photoUrl(row.leave_in_photo_url)"
              @click.stop="openPhotoPreview(photoUrl(row.leave_in_photo_url), 'Leave In')"
              class="h-8 w-8 rounded object-cover border border-border cursor-zoom-in hover:opacity-80 transition-opacity"
              title="Leave In photo"
            />
            <div v-else class="h-8 w-8 rounded bg-muted flex items-center justify-center border border-border" title="No Leave In photo">
              <span class="text-muted-foreground text-[10px]">—</span>
            </div>
            <img
              v-if="row.leave_out_photo_url"
              :src="photoUrl(row.leave_out_photo_url)"
              @click.stop="openPhotoPreview(photoUrl(row.leave_out_photo_url), 'Leave Out')"
              class="h-8 w-8 rounded object-cover border border-border cursor-zoom-in hover:opacity-80 transition-opacity"
              title="Leave Out photo"
            />
            <div v-else class="h-8 w-8 rounded bg-muted flex items-center justify-center border border-border" title="No Leave Out photo">
              <span class="text-muted-foreground text-[10px]">—</span>
            </div>
          </div>
        </template>

        <!-- Status cell -->
        <template #cell-status="{ row }">
          <AppBadge :variant="row.status === 'completed' ? 'success' : row.status === 'active' ? 'warning' : 'muted'">
            {{ row.status }}
          </AppBadge>
        </template>

        <!-- Row actions -->
        <template #actions="{ row }">
          <AppButton size="icon-sm" variant="ghost" @click.stop="openDetail(row)" :title="t('shortLeaveSessions.viewDetail')">
            <Eye class="h-3.5 w-3.5" />
          </AppButton>
        </template>
      </AppTable>

      <!-- Pagination -->
      <div v-if="pagination.total > 0" class="flex items-center justify-between px-4 py-3 border-t border-border">
        <span class="text-sm text-muted-foreground">{{ pagination.total }} {{ t('shortLeaveSessions.totalSessions') }}</span>
        <div class="flex items-center gap-1">
          <AppButton size="icon-sm" variant="outline" :disabled="currentPage <= 1" @click="changePage(currentPage - 1)">
            <ChevronLeft class="h-3.5 w-3.5" />
          </AppButton>
          <span class="px-3 text-sm font-medium text-foreground">{{ currentPage }} / {{ totalPages }}</span>
          <AppButton size="icon-sm" variant="outline" :disabled="currentPage >= totalPages" @click="changePage(currentPage + 1)">
            <ChevronRight class="h-3.5 w-3.5" />
          </AppButton>
        </div>
      </div>
    </AppCard>

    <!-- Photo lightbox -->
    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="photoPreview"
          class="fixed inset-0 bg-black/90 flex items-center justify-center z-[70] p-4"
          @click="photoPreview = null"
        >
          <div class="text-center" @click.stop>
            <p class="text-white/80 text-sm mb-3 font-medium">{{ photoPreviewLabel }}</p>
            <img :src="photoPreview" class="max-w-full max-h-[80vh] rounded-lg shadow-2xl" />
            <button
              @click="photoPreview = null"
              class="mt-4 px-4 py-2 bg-white text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-100"
            >
              {{ t('shortLeaveSessions.close') }}
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Detail modal -->
    <AppModal v-model="selectedSessionOpen" :title="t('shortLeaveSessions.sessionDetail')" size="lg">
      <template v-if="selectedSession">
        <div class="space-y-4">

          <!-- Core info grid -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class="text-xs text-muted-foreground mb-0.5">{{ t('shortLeaveSessions.employee') }}</p>
              <p class="text-sm font-medium text-foreground">{{ selectedSession.employee_name }}</p>
            </div>
            <div>
              <p class="text-xs text-muted-foreground mb-0.5">{{ t('shortLeaveSessions.date') }}</p>
              <p class="text-sm font-medium text-foreground">{{ formatDate(selectedSession.date) }}</p>
            </div>
            <div>
              <p class="text-xs text-muted-foreground mb-0.5">{{ t('shortLeaveSessions.duration') }}</p>
              <div class="text-sm font-medium text-foreground">
                <span v-if="selectedSession.duration_minutes != null">
                  {{ formatDuration(selectedSession.duration_minutes) }}
                  <span v-if="selectedSession.grace_period_applied" class="text-emerald-600 text-xs ml-1">({{ t('shortLeaveSessions.gracePeriod') }})</span>
                </span>
                <span v-else-if="selectedSession.status === 'active'" class="text-amber-500 italic text-xs">{{ t('shortLeaveSessions.inProgress') }}</span>
                <span v-else>—</span>
              </div>
            </div>
            <div>
              <p class="text-xs text-muted-foreground mb-0.5">{{ t('shortLeaveSessions.status') }}</p>
              <AppBadge :variant="selectedSession.status === 'completed' ? 'success' : selectedSession.status === 'active' ? 'warning' : 'muted'">
                {{ selectedSession.status }}
              </AppBadge>
            </div>
          </div>

          <!-- Leave In panel -->
          <div class="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <p class="text-[10px] font-bold text-amber-700 uppercase tracking-widest mb-3">{{ t('shortLeaveSessions.leaveInPanel') }}</p>
            <div class="flex gap-4">
              <div class="shrink-0">
                <img
                  v-if="selectedSession.leave_in_photo_url"
                  :src="photoUrl(selectedSession.leave_in_photo_url)"
                  @click="openPhotoPreview(photoUrl(selectedSession.leave_in_photo_url), 'Leave In')"
                  class="h-20 w-20 rounded-lg border border-amber-200 object-cover cursor-zoom-in hover:opacity-80"
                />
                <div v-else class="h-20 w-20 rounded-lg bg-white border border-amber-200 flex items-center justify-center">
                  <span class="text-xs text-muted-foreground">{{ t('shortLeaveSessions.noPhoto') }}</span>
                </div>
              </div>
              <div class="text-sm space-y-1 flex-1 min-w-0">
                <p>
                  <span class="text-muted-foreground">{{ t('shortLeaveSessions.time') }}:</span>
                  <span class="font-medium ml-1">{{ formatTime(selectedSession.leave_in_time) }}</span>
                  <span v-if="selectedSession.leave_in_source === 'system'" class="ml-1 text-xs text-muted-foreground">({{ t('shortLeaveSessions.system') }})</span>
                </p>
                <p v-if="selectedSession.leave_in_latitude" class="text-xs text-muted-foreground">
                  {{ Number(selectedSession.leave_in_latitude).toFixed(5) }}, {{ Number(selectedSession.leave_in_longitude).toFixed(5) }}
                </p>
                <p v-else class="text-xs text-muted-foreground">{{ t('shortLeaveSessions.locationNotRecorded') }}</p>
              </div>
            </div>
            <template v-if="selectedSession.leave_in_latitude && selectedSession.leave_in_longitude">
              <iframe
                :src="osmEmbedUrl(selectedSession.leave_in_latitude, selectedSession.leave_in_longitude)"
                class="mt-3 w-full rounded-lg border border-amber-200"
                style="height:150px"
                loading="lazy"
                referrerpolicy="no-referrer"
                scrolling="no"
                title="Leave In location"
              ></iframe>
              <a
                :href="`https://www.openstreetmap.org/?mlat=${selectedSession.leave_in_latitude}&mlon=${selectedSession.leave_in_longitude}&zoom=17`"
                target="_blank"
                class="text-xs text-primary hover:underline mt-1 inline-block"
                @click.stop
              >Open full map ↗</a>
            </template>
          </div>

          <!-- Leave Out panel -->
          <div
            class="rounded-lg border p-4"
            :class="selectedSession.leave_out_time ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50/40'"
          >
            <p
              class="text-[10px] font-bold uppercase tracking-widest mb-3"
              :class="selectedSession.leave_out_time ? 'text-emerald-700' : 'text-amber-600'"
            >{{ t('shortLeaveSessions.leaveOutPanel') }}</p>
            <template v-if="selectedSession.leave_out_time">
              <div class="flex gap-4">
                <div class="shrink-0">
                  <img
                    v-if="selectedSession.leave_out_photo_url"
                    :src="photoUrl(selectedSession.leave_out_photo_url)"
                    @click="openPhotoPreview(photoUrl(selectedSession.leave_out_photo_url), 'Leave Out')"
                    class="h-20 w-20 rounded-lg border border-emerald-200 object-cover cursor-zoom-in hover:opacity-80"
                  />
                  <div v-else class="h-20 w-20 rounded-lg bg-white border border-emerald-200 flex items-center justify-center">
                    <span class="text-xs text-muted-foreground">{{ t('shortLeaveSessions.noPhoto') }}</span>
                  </div>
                </div>
                <div class="text-sm space-y-1 flex-1 min-w-0">
                  <p>
                    <span class="text-muted-foreground">Time:</span>
                    <span class="font-medium ml-1">{{ formatTime(selectedSession.leave_out_time) }}</span>
                    <span v-if="selectedSession.leave_out_source === 'system'" class="ml-1 text-xs text-amber-600">({{ t('shortLeaveSessions.auto') }})</span>
                  </p>
                  <p v-if="selectedSession.leave_out_latitude" class="text-xs text-muted-foreground">
                    {{ Number(selectedSession.leave_out_latitude).toFixed(5) }}, {{ Number(selectedSession.leave_out_longitude).toFixed(5) }}
                  </p>
                  <p v-else class="text-xs text-muted-foreground">{{ t('shortLeaveSessions.locationNotRecorded') }}</p>
                </div>
              </div>
              <template v-if="selectedSession.leave_out_latitude && selectedSession.leave_out_longitude">
                <iframe
                  :src="osmEmbedUrl(selectedSession.leave_out_latitude, selectedSession.leave_out_longitude)"
                  class="mt-3 w-full rounded-lg border border-emerald-200"
                  style="height:150px"
                  loading="lazy"
                  referrerpolicy="no-referrer"
                  scrolling="no"
                  title="Leave Out location"
                ></iframe>
                <a
                  :href="`https://www.openstreetmap.org/?mlat=${selectedSession.leave_out_latitude}&mlon=${selectedSession.leave_out_longitude}&zoom=17`"
                  target="_blank"
                  class="text-xs text-primary hover:underline mt-1 inline-block"
                  @click.stop
                >{{ t('shortLeaveSessions.openFullMap') }}</a>
              </template>
            </template>
            <template v-else>
              <AppBadge variant="warning">{{ t('shortLeaveSessions.stillOutBadge') }}</AppBadge>
            </template>
          </div>

        </div>
      </template>
    </AppModal>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { Eye, ChevronLeft, ChevronRight } from 'lucide-vue-next';
import { api } from '@/services/api';
import { AppPageHeader, AppCard, AppTable, AppModal, AppBadge, AppButton } from '@/components/ui';

const { t } = useI18n();

const loading = ref(false);
const sessions = ref([]);
const pagination = ref({ total: 0, pages: 1 });
const currentPage = ref(1);
const selectedSession = ref(null);
const photoPreview = ref(null);
const photoPreviewLabel = ref('');

const filters = ref({
  start_date: new Date(Date.now() - 30 * 86400000).toLocaleDateString('en-CA'),
  end_date: new Date().toLocaleDateString('en-CA'),
  status: ''
});

const columns = computed(() => [
  { key: 'employee_name', label: t('shortLeaveSessions.employee') },
  { key: 'date',          label: t('shortLeaveSessions.date') },
  { key: 'leave_in_time', label: t('shortLeaveSessions.leaveIn') },
  { key: 'leave_out_time', label: t('shortLeaveSessions.leaveOut'), mobileHide: true },
  { key: 'duration_minutes', label: t('shortLeaveSessions.duration') },
  { key: 'photos',        label: t('shortLeaveSessions.photos'), mobileHide: true },
  { key: 'status',        label: t('shortLeaveSessions.status') },
]);

const totalPages = computed(() => pagination.value.pages || 1);

const selectedSessionOpen = computed({
  get: () => selectedSession.value !== null,
  set: (v) => { if (!v) selectedSession.value = null; },
});

const loadSessions = async () => {
  loading.value = true;
  try {
    const params = {
      start_date: filters.value.start_date,
      end_date:   filters.value.end_date,
      page:       currentPage.value,
      limit:      20
    };
    if (filters.value.status) params.status = filters.value.status;

    const res = await api.get('/admin/short-leave-sessions', { params });
    sessions.value   = res.data.data.sessions || [];
    pagination.value = res.data.data.pagination || { total: 0, pages: 1 };
  } catch (err) {
    console.error('Failed to load short leave sessions:', err);
  } finally {
    loading.value = false;
  }
};

const applyFilters = () => {
  currentPage.value = 1;
  loadSessions();
};

const resetFilters = () => {
  filters.value = {
    start_date: new Date(Date.now() - 30 * 86400000).toLocaleDateString('en-CA'),
    end_date:   new Date().toLocaleDateString('en-CA'),
    status: ''
  };
  currentPage.value = 1;
  loadSessions();
};

const changePage = (page) => {
  if (page < 1 || page > totalPages.value) return;
  currentPage.value = page;
  loadSessions();
};

const openDetail = (session) => { selectedSession.value = session; };
const openPhotoPreview = (url, label) => {
  photoPreview.value = url;
  photoPreviewLabel.value = label;
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' }) : '—';
const formatTime = (dt) => dt ? new Date(dt).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit' }) : '—';
const formatDuration = (minutes) => {
  if (minutes == null) return '—';
  const h = Math.floor(minutes / 60), m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const photoUrl = (path) => path ? `${import.meta.env.VITE_API_BASE_URL || ''}/${path}` : null;

const osmEmbedUrl = (lat, lon) => {
  const la = Number(lat), lo = Number(lon), d = 0.003;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${lo-d},${la-d},${lo+d},${la+d}&layer=mapnik&marker=${la},${lo}`;
};

onMounted(loadSessions);
</script>
